import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import * as services from "../../../store/service";
import { useSelector } from "react-redux";
import RenderIcons from "../../../Assets/css/_icons/partner_icon/RenderIcons";
import { ApplicationState } from "../../../store";
import { IPackage } from "../../../store/protectionPlanService/ezs/types";
import { Notification } from "../../common/Notification";
import { useTranslation } from "react-i18next";
import ProtectionPlanTrialCancellationConfirmationComplete from "./ProtectionPlanTrialCancellationConfirmationComplete";

interface IProps {
    closeDialog: any;
    showLogoutDialog: any;
    packageIndex: number;
    isTwoStepCancellation: boolean;
}

const ProtectionPlanTrialCancellationQuestion: React.FunctionComponent<IProps> = (props) => {
    const packageList = useSelector((state: ApplicationState) => {
        if (state.protectionPlanState && state.protectionPlanState.protectionPlanData) {
            return state.protectionPlanState.protectionPlanData.packageList;
        }
    });
    const selectedPackage = (packageList && packageList.length >= props.packageIndex ? packageList[props.packageIndex] : null) as IPackage;
    const isUserPaid = useSelector((state: ApplicationState) =>
        state.myAccountServiceState ? state.myAccountServiceState.familtOffer?.familyAdultOffer?.isUserPaid : false
    );

    const [loading, setLoading] = useState(false);
    const [loadingPaymentInfo, setLoadingPaymentInfo] = useState(false);
    const [paymentMethodDetails, setPaymentMethodDetails] = useState<any>();
    const [paymentInfoState, setPaymentInfoState] = useState({
        name: "",
        cardOrAccNumber: "",
        exp: "",
        paymentMethod: "",
        address: {
            addressline1: "",
            addressline2: "",
            city: "",
            state: "",
            zipCode: "",
        },
    });
    const [isLoader, setLoader] = useState(true);
    const [isCompleted, setCompletion] = useState(false);
    const showLogoutDialog = () => {
        props.showLogoutDialog();
    };
    const { t: tLang } = useTranslation();

    const { handleSubmit } = useForm({
        mode: "onSubmit",
    });

    useEffect(() => {
        window.addEventListener("keyup", (evt: any) => {
            evt = evt || window.event;
            var isEscape = false;
            if ("key" in evt) {
                isEscape = evt.key === "Escape" || evt.key === "Esc";
            } else {
                isEscape = evt.keyCode === 27;
            }
            if (isEscape) {
                props.closeDialog();
            }
        });
        return () => {
            window.removeEventListener("keyup", () => {});
        };
    });

    useEffect(() => {
        getPaymentDetailsData();
    }, []);

    const getPaymentDetailsData = () => {
        if (selectedPackage?.DisplayPaymentInfo) {
            setLoadingPaymentInfo(true);
            services.GetPaymentDetail().then((data) => {
                services.getAvalaraService().then(() => {
                    setCompletion(true);
                });

                setPaymentMethodDetails({ ...paymentMethodDetails, data });
                setLoadingPaymentInfo(false);

                if (data.billingAddress != null) {
                    const billing = data.billingAddress;
                    if (data.creditCard != null && data.creditCard.cardNumber != null) {
                        setPaymentInfoState({
                            ...paymentInfoState,
                            cardOrAccNumber: data.creditCard.cardNumber,
                            name: data.creditCard.nameOnCard,
                            exp: data.creditCard.expDateMo + "/" + data.creditCard.expDateYr,
                            paymentMethod: data.paymentMethod?.selection,
                            address: {
                                addressline1: billing.address1,
                                addressline2: billing.address2,
                                city: billing.city,
                                state: billing.state.selection,
                                zipCode: billing.zipCode,
                            },
                        });
                    } else if (data.eft != null && data.eft.accountNumber != null) {
                        setPaymentInfoState({
                            ...paymentInfoState,
                            cardOrAccNumber: data.eft.accountNumber,
                            name: data.eft.nameOnAccount,
                            paymentMethod: data.paymentMethod?.selection,
                            address: {
                                addressline1: billing.address1,
                                addressline2: billing.address2,
                                city: billing.city,
                                state: billing.state?.selection,
                                zipCode: billing.zipCode,
                            },
                        });
                    }                    
                }
            });
        }
    };

    const getSubscriptionType = () => {
        let subscriptionType = selectedPackage?.SubcriptionType;
        return subscriptionType?.toUpperCase();
    };

    const billingInfoToSubmit = () => {
        if (selectedPackage?.DisplayPaymentInfo) {
            if (!paymentInfoState.paymentMethod && selectedPackage?.ActionButtonText?.toLowerCase() === "cancel") {
                return null;
            }
            return paymentMethodDetails.data;
        }

        return null;
    };

    const onSubmit = (data: any) => {
        const requestBody = {
            packageId: selectedPackage?.PackagId,
            packageCode: selectedPackage?.PackageCode,
            packagePrice: getSubscriptionType() === "YEARLY" ? selectedPackage?.PackagePriceYearly : selectedPackage?.PackagePrice,
            changeType: "CANCEL",
            subscriptionType: getSubscriptionType(),
            billingInformationDTO: billingInfoToSubmit(),
            salesTax: "0.00",
            isPayAsYouGoCurrentPackage: isUserPaid,
        };

        setLoading(true);
        services
            .UpgradeDowngradePackage(requestBody)
            .then((data) => {
                if (!data["hasError"]) {
                    setLoading(false);
                    showLogoutDialog();
                } else {
                    setLoading(false);
                    Notification({
                        type: "error",
                        title: tLang("manageAccount.protectionPlan.notification.errorTitle"),
                        message: tLang("manageAccount.protectionPlan.notification.somethingWentWrongMsg"),
                    });
                }
            })
            .catch(function (error) {
                setLoading(false);
                Notification({
                    type: "error",
                    title: tLang("manageAccount.protectionPlan.notification.errorTitle"),
                    message: tLang("manageAccount.protectionPlan.notification.somethingWentWrongMsg"),
                });
            });
    };

    const cancelTrialPackage = () => {
        const requestBody = {
            packageId: selectedPackage?.PackagId,
            packageCode: selectedPackage?.PackageCode,
            packagePrice: getSubscriptionType() === "YEARLY" ? selectedPackage?.PackagePriceYearly : selectedPackage?.PackagePrice,
            changeType: "CANCEL",
            subscriptionType: getSubscriptionType(),
            billingInformationDTO: billingInfoToSubmit(),
            salesTax: "0.00",
            isPayAsYouGoCurrentPackage: isUserPaid,
        };

        setLoading(true);
        services
            .UpgradeDowngradePackage(requestBody)
            .then((data) => {
                if (!data["hasError"]) {
                    setLoading(false);
                    setCompletion(false);
                    setLoader(false);
                } else {
                    setLoading(false);
                    Notification({
                        type: "error",
                        title: tLang("manageAccount.protectionPlan.notification.errorTitle"),
                        message: tLang("manageAccount.protectionPlan.notification.somethingWentWrongMsg"),
                    });
                }
            })
            .catch(function (error) {
                setLoading(false);
                Notification({
                    type: "error",
                    title: tLang("manageAccount.protectionPlan.notification.errorTitle"),
                    message: tLang("manageAccount.protectionPlan.notification.somethingWentWrongMsg"),
                });
            });
    };

    const displayConfirmationMessage = () => {
        return <p>{tLang("manageAccount.protectionPlan.trialCancellation.QuestionText")}</p>;
    };

    useEffect(() => {
        if (isCompleted && props.isTwoStepCancellation) {
            cancelTrialPackage();
        }
    }, [isCompleted]);


    return (
        <React.Fragment>
            {!props.isTwoStepCancellation ?
                <div className={loading || loadingPaymentInfo ? "loading" : ""}>
                    <div className="dialog-header">
                        <div className="dialog-icon">
                            <RenderIcons iconName="cube" />
                        </div>
                        <h1>{tLang("manageAccount.protectionPlan.trialCancellation.QuestionTitle")}</h1>
                    </div>

                    <div className="dialog-body">{displayConfirmationMessage()}</div>

                    <form onSubmit={handleSubmit(onSubmit)} id="protection-plan-upgrade-form-container">
                        <div className="dialog-footer">
                            <div className={"row between-xs"}>
                                <div className="col-xs col-no-gutter flex-grow-null">
                                    <button
                                        type="button"
                                        className="btn-default btn-link btn-no-padding btn-no-border"
                                        id="btn-cancel-trial-Question-confirmation"
                                        onClick={() => {
                                            window.addToDataLayer({ event: "Protection Plan - Close Trial Question confirmation" });
                                            props.closeDialog();
                                        }}
                                    >
                                        <span className="btn-text">{tLang("manageAccount.protectionPlan.btn.keepMyServices")}</span>
                                    </button>
                                </div>
                                <div className="col-xs col-no-gutter flex-grow-null">
                                    <button
                                        type="submit"
                                        className="btn-default btn-primary"
                                        id="btn-submit-trial-Question-confirmation"
                                        onClick={() => {
                                            window.addToDataLayer({ event: "Protection Plan - Cancel My Trial" });
                                        }}
                                    >
                                        <span className="btn-text">{tLang("manageAccount.protectionPlan.btn.cancelMyTrial")}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
                :
                isLoader ?
                    <div className="col-xs">
                        <div className={`box offset-padding loading`}></div>
                    </div> :
                    <ProtectionPlanTrialCancellationConfirmationComplete
                        packageIndex={props.packageIndex}
                        isTwoStepCancellation={props.isTwoStepCancellation}
                    />
            }
        </React.Fragment>

    );
};
export default ProtectionPlanTrialCancellationQuestion;
